import React, { FC, useState } from 'react'
import {
  Box,
  Stack,
  TableCell,
  Tooltip,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material'
import { TrendingDown, TrendingUp } from '@mui/icons-material'
import {
  Banner,
  DataTable,
  type DataTableColumn,
  NotionPagination,
  PageLoading,
  SubsectionHeader,
} from '@/apps/latest/components/portal/notion'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'
import {
  usePharmacyComparisonQuery,
  usePharmacyListQuery,
} from '@/features/internal/metrics'
import { Dayjs } from 'dayjs'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'

type SortField =
  | 'pharmacy_name'
  | 'average_duration'
  | 'soap_rate'
  | 'copy_rate'
  | 'average_exchange_count'
  | 'speech_rate'
  | 'total_count'
type SortOrder = 'asc' | 'desc'

const PAGE_SIZE = 20

interface ComparisonTabProps {
  startDate: Dayjs | null
  endDate: Dayjs | null
  // 行の薬局の明細 (明細タブ+店舗フィルタ) を開く。比較→明細の唯一の明示導線
  onOpenRecords?: (pharmacyId: string) => void
}

const SORT_COLUMNS: { field: SortField; label: string; align?: 'center' }[] = [
  { field: 'pharmacy_name', label: '薬局名' },
  { field: 'total_count', label: '来局数', align: 'center' },
  { field: 'soap_rate', label: 'SOAP生成率', align: 'center' },
  { field: 'copy_rate', label: 'コピー率', align: 'center' },
  { field: 'average_duration', label: '平均指導時間', align: 'center' },
  {
    field: 'average_exchange_count',
    label: '平均やりとり回数',
    align: 'center',
  },
  { field: 'speech_rate', label: '話速', align: 'center' },
]

const DiffChip: FC<{ diff: string; isPercentage?: boolean }> = ({
  diff,
  isPercentage,
}) => {
  const value = parseFloat(diff)
  const isPositive = value > 0
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.25}
      justifyContent="center"
      sx={{ color: 'text.secondary' }}
    >
      {isPositive ? (
        <TrendingUp sx={{ fontSize: 14 }} />
      ) : (
        <TrendingDown sx={{ fontSize: 14 }} />
      )}
      <Typography sx={PORTAL_TEXT.cellMeta}>
        {diff}
        {isPercentage ? '%' : ''}
      </Typography>
    </Stack>
  )
}

// 旧・利用統計の薬局別比較タブと等価 (意図した差分: 順位絵文字🥇🥈🥉の撤去=無彩色chrome原則、
// 明細への導線追加)。APIは恒久維持の旧GET pharmacy-comparison のまま
export const ComparisonTab: FC<ComparisonTabProps> = ({
  startDate,
  endDate,
  onOpenRecords,
}) => {
  const [sortField, setSortField] = useState<SortField>('average_duration')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  const [currentPage, setCurrentPage] = useState(1)

  // 非本部ユーザーの「全社平均」は自店1行から計算された自店平均であり、
  // 他店のデータは混ざっていない (漏洩ではなくラベルが不正確なだけ)。
  // 自店の値と必ず一致する行を「全社平均」と呼ぶのは誤読を招くので出さない。
  // APIの契約は変えていないため legacy は従来どおり動く (サポート停止方針のため無改修)
  const { pharmacyData } = usePharmacyListQuery()
  const showCompanyAverage = pharmacyData?.has_headquarters_permission ?? false

  const {
    data: comparisonData,
    isLoading: loading,
    error: queryError,
  } = usePharmacyComparisonQuery({
    params: {
      start_date: startDate?.format('YYYY-MM-DD') ?? '',
      end_date: endDate?.format('YYYY-MM-DD') ?? '',
      sort_by: sortField,
      sort_order: sortOrder,
      page: currentPage,
      page_size: PAGE_SIZE,
    },
    enabled: !!startDate && !!endDate,
  })

  const error = queryError?.message ?? null

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('desc')
    }
    setCurrentPage(1)
  }

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page)
  }

  const columns: DataTableColumn[] = SORT_COLUMNS.map((c) => ({
    key: c.field,
    label: (
      <TableSortLabel
        active={sortField === c.field}
        direction={sortField === c.field ? sortOrder : 'asc'}
        onClick={() => handleSort(c.field)}
      >
        {c.label}
      </TableSortLabel>
    ),
    align: c.align,
    // 見出しの語中折返し (「SOAP生/成率」) は可読性を壊すため1行に固定。
    // 本表は overflowX 付きの容器に入っているため横スクロールで吸収される
    headerSx: { whiteSpace: 'nowrap' },
  }))

  return (
    <Stack gap={4}>
      {loading && <PageLoading />}
      {error && <Banner severity="danger">{error}</Banner>}

      {comparisonData && (
        <Box>
          <SubsectionHeader
            heading="薬局別実績"
            actions={
              <Typography sx={PORTAL_TEXT.cellMeta}>
                全{comparisonData?.total_pharmacies || 0}薬局
              </Typography>
            }
          />
          <Box sx={{ overflowX: 'auto' }}>
            <DataTable columns={columns}>
              {comparisonData.pharmacies.map((pharmacy) => (
                <TableRow key={pharmacy.pharmacy_id}>
                  <TableCell>
                    {/* 件数Chipは来局数列と同値 (data_count=total_count) のため置かない — 重複表示の統合 */}
                    <Typography
                      sx={{
                        ...PORTAL_TEXT.cellBody,
                        ...(onOpenRecords && {
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                        }),
                      }}
                      onClick={() => onOpenRecords?.(pharmacy.pharmacy_id)}
                    >
                      {pharmacy.pharmacy_name}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={PORTAL_TEXT.cellBody}>
                      {pharmacy.metrics.total_count}件
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={`${pharmacy.metrics.soap_count || 0}件`}>
                      <Typography
                        sx={{ ...PORTAL_TEXT.cellBody, whiteSpace: 'nowrap' }}
                      >
                        {pharmacy.metrics.soap_rate.toFixed(1)}%
                      </Typography>
                    </Tooltip>
                    {showCompanyAverage && (
                      <DiffChip
                        diff={pharmacy.diff_from_average.soap_rate_diff}
                        isPercentage
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip
                      title={`${pharmacy.metrics.copy_count || 0}/${
                        pharmacy.metrics.soap_count || 0
                      }件 (SOAP生成に対する割合)`}
                    >
                      <Typography
                        sx={{ ...PORTAL_TEXT.cellBody, whiteSpace: 'nowrap' }}
                      >
                        {pharmacy.metrics.copy_rate?.toFixed(1) || 0}%
                      </Typography>
                    </Tooltip>
                    {showCompanyAverage && (
                      <DiffChip
                        diff={pharmacy.diff_from_average.copy_rate_diff || '0'}
                        isPercentage
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={PORTAL_TEXT.cellBody}>
                      {pharmacy.metrics.average_duration}秒
                    </Typography>
                    {showCompanyAverage && (
                      <DiffChip
                        diff={pharmacy.diff_from_average.duration_diff}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={PORTAL_TEXT.cellBody}>
                      {pharmacy.metrics.average_exchange_count.toFixed(1)}回
                    </Typography>
                    {showCompanyAverage && (
                      <DiffChip
                        diff={pharmacy.diff_from_average.exchange_diff}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      sx={{ ...PORTAL_TEXT.cellBody, whiteSpace: 'nowrap' }}
                    >
                      {pharmacy.metrics.speech_rate !== null
                        ? `${pharmacy.metrics.speech_rate.toFixed(0)}文字/分`
                        : '-'}
                    </Typography>
                    {showCompanyAverage &&
                      pharmacy.metrics.speech_rate !== null && (
                        <DiffChip
                          diff={
                            pharmacy.diff_from_average.speech_rate_diff || '0'
                          }
                        />
                      )}
                  </TableCell>
                </TableRow>
              ))}

              {showCompanyAverage && (
                <TableRow sx={{ backgroundColor: colorTokens.bg.subtle }}>
                  <TableCell>
                    <Typography sx={PORTAL_TEXT.cellBody}>全社平均</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={PORTAL_TEXT.cellBody}>-</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={PORTAL_TEXT.cellBody}>
                      {comparisonData.company_average.soap_rate.toFixed(1)}%
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={PORTAL_TEXT.cellBody}>
                      {comparisonData.company_average.copy_rate?.toFixed(1) ||
                        0}
                      %
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={PORTAL_TEXT.cellBody}>
                      {comparisonData.company_average.average_duration}秒
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={PORTAL_TEXT.cellBody}>
                      {comparisonData.company_average.average_exchange_count.toFixed(
                        1
                      )}
                      回
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      sx={{ ...PORTAL_TEXT.cellBody, whiteSpace: 'nowrap' }}
                    >
                      {comparisonData.company_average.speech_rate !== null
                        ? `${comparisonData.company_average.speech_rate.toFixed(
                            0
                          )}文字/分`
                        : '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </DataTable>
          </Box>

          {comparisonData.total_pharmacies > PAGE_SIZE && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <NotionPagination
                count={Math.ceil(comparisonData.total_pharmacies / PAGE_SIZE)}
                page={currentPage}
                onChange={handlePageChange}
              />
            </Box>
          )}
        </Box>
      )}
    </Stack>
  )
}
